import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import OutreachModal from "@/components/OutreachModal";
import RevisitModal from "@/components/RevisitModal";
import { RISK } from "@/components/PatientPicker";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MessageSquareText, FileHeart, Loader2, ShieldCheck, Filter, Mail, Search } from "lucide-react";

export default function Retention() {
  const [flags, setFlags] = useState([]);
  const [filter, setFilter] = useState("all");
  const [outreach, setOutreach] = useState(null);
  const [maintaining, setMaintaining] = useState(null);
  const [revisit, setRevisit] = useState(null);
  const [nudging, setNudging] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(() => api.get("/retention").then((r) => setFlags(r.data.flags)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const needle = q.trim().toLowerCase();
  const shown = flags.filter((f) =>
    (filter === "all" || f.risk === filter) &&
    (!needle || `${f.name} ${f.cliniko_id} ${f.clinician || ""} ${f.diagnosis || ""}`.toLowerCase().includes(needle)));

  const maintenancePlan = async (f) => {
    setMaintaining(f.cliniko_id);
    try {
      const { data } = await api.post(`/retention/${f.cliniko_id}/maintenance-plan`);
      const saved = await api.post("/plans", data);
      toast.success("Maintenance plan drafted & saved to history");
      setRevisit({ plan: saved.data, patientName: f.name });
    } catch { toast.error("Could not generate maintenance plan"); }
    finally { setMaintaining(null); }
  };

  const nudge = async (f) => {
    if (!f.email) { toast.error("No email on file for this patient"); return; }
    if (!window.confirm(`Send a booking reminder email to ${f.name} (${f.email})?`)) return;
    setNudging(f.cliniko_id);
    try {
      const { data } = await api.post(`/retention/${f.cliniko_id}/nudge`, {});
      toast.success(`Reminder emailed to ${data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send email"); }
    finally { setNudging(null); }
  };

  return (
    <Layout title="Retention Engine" subtitle="Patients to re-engage — flagged from Cliniko visit cadence">
      <div className="mb-4 flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
        <ShieldCheck className="h-4 w-4" /> Flags are suggestions. Confirm clinically before contacting. No auto-send.
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="retention-search" placeholder="Search by patient name, ID or clinician"
          className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500" />
      </div>

      <div className="mb-4 flex items-center gap-2">
        <Filter className="h-4 w-4 text-slate-400" />
        {["all", "high", "medium", "routine"].map((r) => (
          <button key={r} onClick={() => setFilter(r)} data-testid={`retention-filter-${r}`}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition-all ${filter === r ? "bg-teal-600 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-slate-300"}`}>
            {r}{r !== "all" && ` (${flags.filter((f) => f.risk === r).length})`}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3">Patient</th><th className="px-4 py-3">Reason</th>
              <th className="px-4 py-3">Risk</th><th className="px-4 py-3 text-right">Overdue</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((f) => (
              <tr key={f.cliniko_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`retention-patient-row-${f.cliniko_id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-800">{f.name}</div>
                  <div className="font-mono text-[11px] text-slate-400">{f.cliniko_id} · {f.clinician}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-slate-600">{f.diagnosis}</div>
                  <div className="text-xs text-slate-400">{f.reason}</div>
                </td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${RISK[f.risk]}`}>{f.risk}</span></td>
                <td className="px-4 py-3 text-right font-mono font-semibold tabular-nums text-slate-700">{f.days_overdue}d</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setOutreach(f)} data-testid={`outreach-open-${f.cliniko_id}`} className="border-teal-200 text-teal-700 hover:bg-teal-50">
                      <MessageSquareText className="mr-1 h-3.5 w-3.5" /> Outreach
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => nudge(f)} disabled={nudging === f.cliniko_id || !f.email} data-testid={`nudge-${f.cliniko_id}`} className="border-indigo-200 text-indigo-700 hover:bg-indigo-50" title={f.email ? `Email ${f.email}` : "No email on file"}>
                      {nudging === f.cliniko_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Mail className="mr-1 h-3.5 w-3.5" /> Nudge</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => maintenancePlan(f)} disabled={maintaining === f.cliniko_id} data-testid={`maintenance-${f.cliniko_id}`}>
                      {maintaining === f.cliniko_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileHeart className="mr-1 h-3.5 w-3.5" /> Maintenance</>}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-sm text-slate-400">No patients match your search or filter.</td></tr>}
          </tbody>
        </table>
      </div>

      {outreach && <OutreachModal open={!!outreach} onOpenChange={(o) => !o && setOutreach(null)} patient={outreach} />}
      {revisit && <RevisitModal open={!!revisit} onOpenChange={(o) => !o && setRevisit(null)} plan={revisit.plan} patientName={revisit.patientName} />}
    </Layout>
  );
}
